import { useAuthentication, useQueryParam } from '@psyomics/ui-common';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { fetchMfaStatus } from '../../../features/mfa-setup/mfa-client';

type MfaStatus = {
  mfaIsComplete: boolean | null;
  hasPhoneNumber: boolean | null;
  error: Error | null;
};

const useMfaStatus = ({
  apiUrl,
  isAuthenticated,
  isLoading,
  isMfaSetupRoute,
}: {
  apiUrl: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  isMfaSetupRoute: boolean;
}): MfaStatus => {
  const [mfaIsComplete, setMfaIsComplete] = useState<boolean | null>(null);
  const [hasPhoneNumber, setHasPhoneNumber] = useState<boolean | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    setMfaIsComplete(null);
    setHasPhoneNumber(null);

    fetchMfaStatus(apiUrl)
      .then((response) => {
        setHasPhoneNumber(response.hasPhoneNumber);
        setMfaIsComplete(response.mfaComplete);
      })
      .catch((err) => setError(new Error(String(err))));
  }, [apiUrl, isAuthenticated, isLoading, isMfaSetupRoute]);

  return { mfaIsComplete, hasPhoneNumber, error };
};

const getSetupUrl = (hasPhoneNumber: boolean, redirect: string) => {
  const params = new URLSearchParams({ redirect });
  if (!hasPhoneNumber) params.append('require-phone', '');
  return `/mfa-setup?${params.toString()}`;
};

const getMfaRedirect = ({
  isAuthenticated,
  isLoading,
  mfaIsComplete,
  hasPhoneNumber,
  isMfaSetupRoute,
  pathname,
  redirectParam,
}: {
  isAuthenticated: boolean;
  isLoading: boolean;
  mfaIsComplete: boolean | null;
  hasPhoneNumber: boolean | null;
  isMfaSetupRoute: boolean;
  pathname: string;
  redirectParam?: string;
}) => {
  if (!isAuthenticated || isLoading || mfaIsComplete === null || hasPhoneNumber === null) return null;
  if (mfaIsComplete && isMfaSetupRoute) return redirectParam ?? pathname;
  if (!mfaIsComplete && !isMfaSetupRoute) return getSetupUrl(hasPhoneNumber, pathname);
  return null;
};

const RequireMfaSetup = () => {
  const { apiUrl, isAuthenticated, isLoading } = useAuthentication();
  const { pathname } = useLocation();
  const redirectParam = useQueryParam('redirect');
  const isMfaSetupRoute = pathname === '/mfa-setup';
  const { mfaIsComplete, hasPhoneNumber, error } = useMfaStatus({
    apiUrl,
    isAuthenticated,
    isLoading,
    isMfaSetupRoute,
  });
  const isPending = !isAuthenticated || isLoading || mfaIsComplete === null || hasPhoneNumber === null;
  const redirectTo = useMemo(() => {
    if (error) return null;
    const redirect = getMfaRedirect({
      hasPhoneNumber,
      isAuthenticated,
      isLoading,
      isMfaSetupRoute,
      mfaIsComplete,
      pathname,
      redirectParam,
    });
    return redirect;
  }, [error, hasPhoneNumber, isAuthenticated, isLoading, redirectParam, isMfaSetupRoute, mfaIsComplete, pathname]);

  if (error) throw error;
  if (isPending) return null;
  if (redirectTo) {
    return <Navigate replace to={redirectTo} />;
  }
  return <Outlet />;
};

export { RequireMfaSetup };
